import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Box, Flex, Tooltip, Input, Button, Text, Select, IconButton, useToast } from "@chakra-ui/react";
import { FaFileExcel, FaListUl, FaTh } from "react-icons/fa";
import CustomDatePicker from "@/components/CustomDatePicker";
import DebouncedAutocompleteInput from "@/components/FinancialShared/DebouncedAutocompleteInput";
import {
  ConsigConciliationFilters,
  initialConsigConciliationFilters,
} from "@/models/consig-conciliation-filters.model";
import { ConciliacaoStatus, ViewModeType } from "@/models/consignado/propostaFinanceira.model";
import { downloadConciliationExcel } from "@/services/financial-service/conciliation.service";
import {
  searchPromoters,
  searchCorbans,
  searchCommercialManagers,
} from "@/services/consignado/financial-service/autocomplete.service";
import { searchVendedores } from "@/services/financial-service/global-state.service";

interface ConciliationFiltersProps {
  backgroundStyle: Record<string, unknown>;
  filters: ConsigConciliationFilters;
  setFilters: Dispatch<SetStateAction<ConsigConciliationFilters>>;
  onApplyFilters?: (filters: ConsigConciliationFilters) => void;
  handleOpenConfigModal: () => void
  handleChangeModeView: (viewMode: ViewModeType) => void
  viewMode: ViewModeType
}

type FilterField = Exclude<keyof ConsigConciliationFilters, "dateRange">;

export default function ConciliationFilters({
  backgroundStyle,
  filters,
  setFilters,
  onApplyFilters,
  handleOpenConfigModal,
  handleChangeModeView,
  viewMode
}: ConciliationFiltersProps) {
  const toast = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const [pickerDateRange, setPickerDateRange] = useState<[Date | null, Date | null]>([null, null]);

  // Estados para inputs de autocomplete com debounce
  const [promotoraInput, setPromotoraInput] = useState(filters.promotora);
  const [corbanInput, setCorbanInput] = useState(filters.corban);
  const [vendedorInput, setVendedorInput] = useState(filters.vendedor);
  const [gerenteInput, setGerenteInput] = useState(filters.gerenteComercial);

  // Sincroniza inputs se os filtros externos mudarem (ex: ao limpar)
  useEffect(() => {
    setPromotoraInput(filters.promotora);
    setCorbanInput(filters.corban);
    setVendedorInput(filters.vendedor);
    setGerenteInput(filters.gerenteComercial);
  }, [filters.promotora, filters.corban, filters.vendedor, filters.gerenteComercial]);

  useEffect(() => {
    const [startDate, endDate] = filters.dateRange ?? [];

    setPickerDateRange([
      startDate ? new Date(startDate) : null,
      endDate ? new Date(endDate) : null,
    ]);
  }, [filters.dateRange]);

  const parseDateRangeToStrings = (dates: [Date | null, Date | null]): string[] => {
    const [startDate, endDate] = dates;

    if (!startDate && !endDate)
      return [];

    if (startDate && !endDate)
      return [startDate.toISOString()];

    return [startDate?.toISOString() ?? "", endDate?.toISOString() ?? ""].filter(Boolean);
  };

  const handleFilterChange = ({ key, value }: { key: FilterField; value: string }): void => {
    setFilters((previousFilters) => ({
      ...previousFilters,
      [key]: value,
    }));
  };

  const handleClearFilters = (): void => {
    setPickerDateRange([null, null]);
    setFilters(initialConsigConciliationFilters);
  };

  const handleApplyFilters = (): void => {
    if (!onApplyFilters)
      return;

    onApplyFilters(filters);
  };

  const handleDownload = async () => {
    const [startDate, endDate] = pickerDateRange;

    if (!startDate || !endDate) {
      toast({
        title: "Selecione o intervalo de datas",
        description: "Por favor, selecione um intervalo de datas para gerar o relatório.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsDownloading(true);

    try {
      const blob = await downloadConciliationExcel(
        startDate,
        endDate,
        []
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "conciliado.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Sucesso",
        description: "O relatório foi gerado e baixado com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Erro ao baixar o relatório:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao gerar o relatório. Por favor, tente novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box
      {...backgroundStyle}
      borderRadius="xl"
      p={{ base: 4, md: 6 }}
      color="white"
      boxShadow="lg"
      width="100%"
      mb={2}
      h="auto"
    >
      <Text fontSize="xl" fontWeight="bold" fontFamily="Lexend Deca" mb={4}>
        FILTROS DE CONCILIAÇÃO
      </Text>

      <Flex flexDirection="column" gap={4}>
        <Flex gap={4}>
          <CustomDatePicker
            dateRange={pickerDateRange}
            onChange={(dates) => {
              setPickerDateRange(dates);
              setFilters((previousFilters) => ({
                ...previousFilters,
                dateRange: parseDateRangeToStrings(dates),
              }));
            }}
          />

          <Box width={{ base: "100%", md: "70%", lg: "20%" }} mt={{ base: 5, md: 0 }} flex={1}>
            <DebouncedAutocompleteInput
              placeholder="Filtrar por Promotora"
              tooltip="Filtro de Promotora por Nome Fantasia"
              inputValue={promotoraInput}
              onInputChange={(val) => {
                setPromotoraInput(val);
                setFilters((prev) => ({ ...prev, promotora: "" }));
              }}
              onSelect={(label) => {
                setPromotoraInput(label);
                setFilters((prev) => ({ ...prev, promotora: label }));
              }}
              onClear={() => {
                setPromotoraInput("");
                setFilters((prev) => ({ ...prev, promotora: "" }));
              }}
              fetchOptions={searchPromoters}
            />
          </Box>

          <Box width={{ base: "100%", md: "70%", lg: "20%" }} mt={{ base: 5, md: 0 }} flex={1}>
            <DebouncedAutocompleteInput
              placeholder="Filtrar por Corban"
              tooltip="Filtro de Corban por Nome Fantasia"
              inputValue={corbanInput}
              onInputChange={(val) => {
                setCorbanInput(val);
                setFilters((prev) => ({ ...prev, corban: "" }));
              }}
              onSelect={(label) => {
                setCorbanInput(label);
                setFilters((prev) => ({ ...prev, corban: label }));
              }}
              onClear={() => {
                setCorbanInput("");
                setFilters((prev) => ({ ...prev, corban: "" }));
              }}
              fetchOptions={searchCorbans}
            />
          </Box>

          <Box width={{ base: "100%", md: "70%", lg: "20%" }} mt={{ base: 5, md: 0 }} flex={1}>
            <DebouncedAutocompleteInput
              placeholder="Filtrar por Vendedor"
              tooltip="Filtro de Vendedor por Nome"
              inputValue={vendedorInput}
              onInputChange={(val) => {
                setVendedorInput(val);
                setFilters((prev) => ({ ...prev, vendedor: "" }));
              }}
              onSelect={(label) => {
                setVendedorInput(label);
                setFilters((prev) => ({ ...prev, vendedor: label }));
              }}
              onClear={() => {
                setVendedorInput("");
                setFilters((prev) => ({ ...prev, vendedor: "" }));
              }}
              fetchOptions={searchVendedores}
            />
          </Box>

          <Box width={{ base: "100%", md: "70%", lg: "20%" }} mt={{ base: 5, md: 0 }} flex={1}>
            <DebouncedAutocompleteInput
              placeholder="Filtrar por Gerente Comercial"
              tooltip="Filtro de Gerente Comercial por Nome"
              inputValue={gerenteInput}
              onInputChange={(val) => {
                setGerenteInput(val);
                setFilters((prev) => ({ ...prev, gerenteComercial: "" }));
              }}
              onSelect={(label) => {
                setGerenteInput(label);
                setFilters((prev) => ({ ...prev, gerenteComercial: label }));
              }}
              onClear={() => {
                setGerenteInput("");
                setFilters((prev) => ({ ...prev, gerenteComercial: "" }));
              }}
              fetchOptions={searchCommercialManagers}
            />
          </Box>
        </Flex>

        <Flex gap={4} flexDirection={{ base: "column", md: "row" }}>
          <Box flex={1}>
            <Tooltip label="Filtro da Proposta pelo Id ou Shorthash" fontSize="md" placement="bottom">
              <Input
                placeholder="Filtrar por Id ou Shorthash"
                bg="white"
                height="3rem"
                color="black"
                fontFamily="Lexend Deca"
                fontSize="1rem"
                value={filters.proposalIdOrShorthash}
                onChange={(event) =>
                  handleFilterChange({ key: "proposalIdOrShorthash", value: event.target.value })
                }
              />
            </Tooltip>
          </Box>

          <Box flex={1}>
            <Tooltip label="Filtro da Proposta pelo Status da Conciliação" fontSize="md" placement="bottom">
              <Select
                h="3rem"
                bg="white"
                color="black"
                fontFamily="Lexend Deca"
                fontSize="1rem"
                value={filters.conciliationStatus}
                onChange={(event) =>
                  handleFilterChange({ key: "conciliationStatus", value: event.target.value })
                }
              >
                <option value="">Filtrar pelo Status da Conciliação</option>
                <option value={ConciliacaoStatus.PENDENTE}>Pendente</option>
                <option value={ConciliacaoStatus.CONCLUIDO}>Concluído</option>
                <option value={ConciliacaoStatus.DIVERGENTE}>Divergente</option>
              </Select>
            </Tooltip>
          </Box>
        </Flex>
      </Flex>

      <Flex width="100%" alignItems='center' justifyContent="space-between" mt={4}>
        <Flex alignItems='center' gap={4}>
          <Button colorScheme="secondary" onClick={handleOpenConfigModal}>
            Configurações da Conciliação
          </Button>
          <Button
            colorScheme="green"
            textTransform="uppercase"
            onClick={handleDownload}
            isLoading={isDownloading}
            loadingText="Gerando..."
            leftIcon={<FaFileExcel />}
            height="3rem"
          >
            Gerar Relatório Excel
          </Button>
        </Flex>
  
        <Flex alignItems='center' gap={4}>
          <Button variant="ghost" colorScheme="primary" onClick={handleClearFilters}>
            Limpar Filtros
          </Button>
          <Button colorScheme="primary" onClick={handleApplyFilters}>
            Filtrar
          </Button>
          <IconButton
            icon={<FaTh />}
            aria-label="List View"
            onClick={() => handleChangeModeView("detail")}
            colorScheme="whiteAlpha"
            variant={viewMode === "detail" ? "outline" : "solid"}
          />
          <IconButton
            icon={<FaListUl />}
            aria-label="Detail View"
            onClick={() => handleChangeModeView("list")}
            colorScheme="whiteAlpha"
            variant={viewMode === "list" ? "outline" : "solid"}
          />
        </Flex>
      </Flex>
    </Box>
  );
}
